/** @format */

// Decides HOW a chart payload should actually be presented, regardless of the
// chart type the backend suggested. A single data point as a pie chart is
// meaningless — show a big stat instead; 2–3 categories deserve a compact
// comparison, trends deserve a line, and very wide data belongs in a table.
// Pure function, no React/MUI — trivially testable.

// Month names, ISO dates (2025-06 / 2025-06-04), dd/mm/yyyy, "Week 23", Q1 etc.
const TEMPORAL_PATTERNS = [
  /^\d{4}-\d{2}(-\d{2})?([T\s].*)?$/, // ISO date or datetime
  /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/, // dd/mm/yyyy
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*([\s,-]+\d{1,4})?$/i,
  /^(week|wk|w)\s*-?\s*\d{1,2}$/i,
  /^q[1-4]([\s,-]+\d{4})?$/i,
  /^\d{4}$/, // bare year
];

export const isTemporalValue = (v) => {
  if (v == null) return false;
  const s = String(v).trim();
  return TEMPORAL_PATTERNS.some((re) => re.test(s));
};

// A series is temporal when (almost) every x value looks like a date/period.
const isTemporalSeries = (values) => {
  if (!values.length) return false;
  const hits = values.filter(isTemporalValue).length;
  return hits / values.length >= 0.8;
};

const prettifyKey = (key) =>
  String(key || "")
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

/**
 * @param {object} chart backend chart payload
 *   { type, data, x_key, y_keys, value_key, name_key, title }
 * @returns {{
 *   mode: 'none'|'bigstat'|'comparison'|'chart',
 *   recommendedChart: 'bar'|'line'|'area'|'pie'|null,
 *   allowPie: boolean,
 *   bigStat: { value, label, context }|null,
 *   xKey: string|null,
 *   yKeys: string[],
 * }}
 */
export default function resolveVisualization(chart) {
  const none = {
    mode: "none",
    recommendedChart: null,
    allowPie: false,
    bigStat: null,
    xKey: null,
    yKeys: [],
  };

  if (
    !chart ||
    chart.type === "none" ||
    !Array.isArray(chart.data) ||
    chart.data.length === 0
  ) {
    return none;
  }

  const { type, data, x_key, y_keys, name_key, value_key, title } = chart;
  // Same key-fallback logic ChartView has always used.
  const xKey = x_key || name_key || Object.keys(data[0] || {})[0] || null;
  const yKeysRaw =
    y_keys && y_keys.length > 0 ? y_keys : value_key ? [value_key] : [];
  const yKeys = yKeysRaw.filter(Boolean);

  if (!xKey || yKeys.length === 0) return none;

  const rowCount = data.length;
  const xValues = data.map((row) => row?.[xKey]);
  const temporal = isTemporalSeries(xValues);

  // ── One data point, one measure → a chart adds nothing. Big number. ───────
  if (rowCount === 1 && yKeys.length === 1) {
    const row = data[0] || {};
    return {
      mode: "bigstat",
      recommendedChart: null,
      allowPie: false,
      bigStat: {
        value: row[yKeys[0]],
        label: title || prettifyKey(yKeys[0]),
        context: row[xKey] != null ? String(row[xKey]) : null,
      },
      xKey,
      yKeys,
    };
  }

  // ── 2–3 categorical points, one measure → compact side-by-side bars. ──────
  if (!temporal && rowCount <= 3 && yKeys.length === 1) {
    return {
      mode: "comparison",
      recommendedChart: "bar",
      allowPie: false,
      bigStat: null,
      xKey,
      yKeys,
    };
  }

  // ── Real chart territory. Pick the most honest default. ───────────────────
  let recommendedChart;
  if (temporal && rowCount >= 4) {
    // Trend over time → line (keep area if the backend explicitly asked).
    recommendedChart = type === "area" ? "area" : "line";
  } else if (["bar", "grouped_bar", "stacked_bar"].includes(type)) {
    recommendedChart = "bar";
  } else if (["line", "area", "pie"].includes(type)) {
    recommendedChart = type;
  } else {
    recommendedChart = "bar";
  }

  // Pie only for one measure across a handful of categories — and never for
  // a time series (a pie of months is meaningless).
  const allowPie =
    !temporal && yKeys.length === 1 && rowCount >= 2 && rowCount <= 12;
  if (recommendedChart === "pie" && !allowPie) recommendedChart = "bar";

  return {
    mode: "chart",
    recommendedChart,
    allowPie,
    bigStat: null,
    xKey,
    yKeys,
  };
}
